import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  HiveExecution,
  HiveApproval,
  HiveWorkflow,
  ServiceHealth,
  ExecStatus,
  ExecStep,
} from "@/types";
import { nodeDef } from "@/data/mockWorkflowNodes";
import { useHive } from "@/store/useHive";
import { api } from "@/api/client";
import type { Paged } from "@/api/types";
import { uid } from "@/lib/utils";

interface ExecutionState {
  executions: HiveExecution[];
  selectedExecutionId: string | null;
  selectedStepId: string | null;
  statusFilter: ExecStatus | "all";
  workflowFilter: string | null;
  centerView: "executions" | "approvals" | "health";

  approvals: HiveApproval[];
  approvalModalId: string | null;

  systemHealth: ServiceHealth[];

  hydrate: () => Promise<void>;

  selectExecution: (id: string | null) => void;
  setCenterView: (v: "executions" | "approvals" | "health") => void;
  selectStep: (id: string | null) => void;
  setStatusFilter: (f: ExecStatus | "all") => void;
  setWorkflowFilter: (id: string | null) => void;
  openApprovalModal: (id: string | null) => void;

  createFromWorkflow: (wf: HiveWorkflow, sandbox: boolean) => HiveExecution;
  pauseExecution: (id: string) => void;
  resumeExecution: (id: string) => void;
  cancelExecution: (id: string) => void;
  retryStep: (execId: string, stepId: string) => void;
  rerunFromStep: (execId: string, stepId: string) => void;
  exportLogs: (id: string) => void;

  approve: (approvalId: string) => void;
  reject: (approvalId: string, reason: string) => void;
  escalate: (approvalId: string) => void;

  tick: () => void;
}

function progressOf(steps: ExecStep[]) {
  if (!steps.length) return 0;
  const done = steps.filter((s) => s.status === "completed" || s.status === "skipped").length;
  const running = steps.some((s) => s.status === "running") ? 0.5 : 0;
  return Math.min(1, (done + running) / steps.length);
}

interface ServerRunner {
  id: string;
  name: string | null;
  status: "connected" | "disconnected";
  lastSeen: string | null;
  metadata: Record<string, unknown>;
}

/** Health view = the org's real runner registry. Only fields the control plane
 *  actually reports are surfaced — no synthesized latency/cost metrics. */
const runnerHealth = (r: ServerRunner): ServiceHealth => ({
  id: r.id,
  name: r.name ?? r.id,
  category: "Infra",
  status: r.status === "connected" ? "healthy" : "offline",
  latency: "—",
  queueDepth: 0,
  errorRate: 0,
  costToday: 0,
  activeJobs: 0,
  spark: [],
});

export const useExecution = create<ExecutionState>()(
  persist(
    (set, get) => {
      const syncError = (body = "Change wasn't saved — retry") =>
        useHive.getState().pushToast({ title: "Sync failed", body, tone: "error" });

      /** Push the client's copy of an execution to the server (fire-and-forget). */
      const persistExecution = (id: string) => {
        const exec = get().executions.find((e) => e.id === id);
        if (!exec) return;
        api.patch<HiveExecution>(`/api/v1/executions/${id}`, exec).catch(() => syncError());
      };

      /** Optimistic status change + server action route. */
      const execAction = (id: string, action: "pause" | "resume" | "cancel", status: ExecStatus) => {
        set((s) => ({ executions: s.executions.map((e) => (e.id === id ? { ...e, status } : e)) }));
        api.post(`/api/v1/executions/${id}/${action}`).catch(() => syncError());
      };

      return {
        executions: [],
        selectedExecutionId: null,
        selectedStepId: null,
        statusFilter: "all",
        workflowFilter: null,
        centerView: "executions",

        approvals: [],
        approvalModalId: null,

        systemHealth: [],

        hydrate: async () => {
          try {
            const [ex, ap, rn] = await Promise.all([
              api.get<Paged<HiveExecution>>("/api/v1/executions?limit=100"),
              api.get<Paged<HiveApproval>>("/api/v1/approvals?limit=100"),
              api.get<{ items: ServerRunner[] }>("/api/v1/runners"),
            ]);
            set((s) => ({
              executions: ex.items,
              approvals: ap.items,
              systemHealth: rn.items.map(runnerHealth),
              selectedExecutionId: ex.items.some((e) => e.id === s.selectedExecutionId)
                ? s.selectedExecutionId
                : ex.items[0]?.id ?? null,
            }));
          } catch {
            syncError("Couldn't load executions — retry");
          }
        },

        selectExecution: (id) => set({ selectedExecutionId: id, selectedStepId: null }),
        setCenterView: (v) => set({ centerView: v }),
        selectStep: (id) => set({ selectedStepId: id }),
        setStatusFilter: (f) => set({ statusFilter: f }),
        setWorkflowFilter: (id) => set({ workflowFilter: id, statusFilter: "all" }),
        openApprovalModal: (id) => set({ approvalModalId: id }),

        createFromWorkflow: (wf, sandbox) => {
          const order = { hook: 0, process: 1, publish: 2 };
          const ordered = [...wf.nodes]
            .filter((n) => !n.disabled)
            .sort((a, b) => {
              const ka = order[nodeDef(a.defKey).kind];
              const kb = order[nodeDef(b.defKey).kind];
              return ka - kb || a.y - b.y;
            });
          const steps: ExecStep[] = ordered.map((n, i) => {
            const d = nodeDef(n.defKey);
            let status: ExecStep["status"] = "pending";
            if (i === 0) status = "completed";
            else if (i === 1) status = "running";
            return {
              id: uid("s"),
              label: n.title,
              kind: d.kind,
              status,
              at: i <= 1 ? "now" : "—",
              duration: i === 0 ? "0.3s" : i === 1 ? "running" : "—",
              agent: d.ai ? "Sandbox-Sim" : undefined,
              cost: undefined,
              log: i === 1 ? "Simulating…" : undefined,
            };
          });
          const exec: HiveExecution = {
            id: uid("ex"),
            name: `${sandbox ? "Sandbox · " : ""}${wf.name}`,
            workflowId: wf.id,
            workflowName: wf.name,
            status: "running",
            currentStep: steps[1]?.label ?? steps[0]?.label ?? "—",
            progress: progressOf(steps),
            startedAt: "now",
            duration: "0m 02s",
            owner: "You",
            cost: 0,
            risk: wf.risk,
            triggerEvent: `${wf.triggerHook} · ${sandbox ? "sample event" : "live"}`,
            steps,
            artifacts: [],
            permissionsUsed: sandbox ? ["sandbox:simulate"] : [],
            logs: [
              { time: "now", level: "info", text: `${sandbox ? "Sandbox " : ""}run started for ${wf.name}` },
            ],
            simulated: true,
          };
          set((s) => ({
            executions: [exec, ...s.executions],
            selectedExecutionId: exec.id,
            workflowFilter: null,
          }));
          // Record the run server-side; adopt the server-assigned id.
          const { id: _localId, ...body } = exec;
          api
            .post<HiveExecution>("/api/v1/executions", body)
            .then((created) =>
              set((s) => ({
                executions: s.executions.map((e) => (e.id === exec.id ? { ...e, ...created, simulated: true } : e)),
                selectedExecutionId: s.selectedExecutionId === exec.id ? created.id : s.selectedExecutionId,
              }))
            )
            .catch(() => syncError("Run wasn't recorded — retry"));
          return exec;
        },

        pauseExecution: (id) => {
          execAction(id, "pause", "paused");
          useHive.getState().pushToast({ title: "Execution paused", body: name(get, id), tone: "warning" });
        },
        resumeExecution: (id) => {
          execAction(id, "resume", "running");
          useHive.getState().pushToast({ title: "Execution resumed", body: name(get, id), tone: "success" });
        },
        cancelExecution: (id) => {
          execAction(id, "cancel", "cancelled");
          useHive.getState().pushToast({ title: "Execution cancelled", body: name(get, id), tone: "info" });
        },

        retryStep: (execId, stepId) => {
          set((s) => ({
            executions: s.executions.map((e) => {
              if (e.id !== execId) return e;
              const steps = e.steps.map((st) =>
                st.id === stepId
                  ? { ...st, status: "running" as const, error: undefined, retries: (st.retries ?? 0) + 1, at: "now", duration: "running" }
                  : st
              );
              return { ...e, steps, status: "running", currentStep: steps.find((x) => x.id === stepId)?.label ?? e.currentStep };
            }),
          }));
          persistExecution(execId);
          useHive.getState().pushToast({ title: "Retrying step", body: name(get, execId), tone: "info" });
        },

        rerunFromStep: (execId, stepId) => {
          set((s) => ({
            executions: s.executions.map((e) => {
              if (e.id !== execId) return e;
              let reached = false;
              const steps = e.steps.map((st) => {
                if (st.id === stepId) reached = true;
                if (reached) return { ...st, status: st.id === stepId ? ("running" as const) : ("pending" as const), error: undefined };
                return st;
              });
              return { ...e, steps, status: "running", progress: progressOf(steps) };
            }),
          }));
          persistExecution(execId);
          useHive.getState().pushToast({ title: "Re-running from step", body: name(get, execId), tone: "info" });
        },

        exportLogs: (id) =>
          useHive.getState().pushToast({ title: "Logs exported", body: `${name(get, id)} · logs.ndjson`, tone: "success" }),

        approve: (approvalId) => {
          const ap = get().approvals.find((a) => a.id === approvalId);
          if (!ap) return;
          api
            .post<HiveApproval>(`/api/v1/approvals/${approvalId}/approve`)
            .then((decided) => {
              set((s) => ({
                approvals: s.approvals.map((a) => (a.id === approvalId ? { ...a, ...decided } : a)),
                approvalModalId: null,
                executions: s.executions.map((e) => {
                  if (e.id !== ap.executionId) return e;
                  let advanced = false;
                  const steps = e.steps.map((st) => {
                    if (st.status === "waiting-approval") {
                      advanced = true;
                      return { ...st, status: "completed" as const, at: "now", duration: "1s", log: "Approved" };
                    }
                    if (advanced && st.status === "pending") {
                      advanced = false;
                      return { ...st, status: "running" as const, at: "now", duration: "running" };
                    }
                    return st;
                  });
                  return {
                    ...e,
                    steps,
                    status: "running",
                    progress: progressOf(steps),
                    currentStep: steps.find((x) => x.status === "running")?.label ?? e.currentStep,
                  };
                }),
              }));
              persistExecution(ap.executionId);
              useHive.getState().pushToast({ title: "Approved", body: `${ap.action} · ${ap.workflowName}`, tone: "success" });
              useHive.getState().pushNotification({ title: "Action approved", body: `${ap.action} for ${ap.executionName}.`, tone: "success", app: "execution" });
              useHive.getState().pushActivity({ kind: "job", title: "Approval granted", detail: `${ap.action}`, app: "execution" });
            })
            .catch(() => syncError("Approval wasn't recorded — retry"));
        },

        reject: (approvalId, reason) => {
          const ap = get().approvals.find((a) => a.id === approvalId);
          if (!ap) return;
          api
            .post<HiveApproval>(`/api/v1/approvals/${approvalId}/reject`)
            .then((decided) => {
              set((s) => ({
                approvals: s.approvals.map((a) => (a.id === approvalId ? { ...a, ...decided } : a)),
                approvalModalId: null,
                executions: s.executions.map((e) =>
                  e.id === ap.executionId
                    ? {
                        ...e,
                        status: "failed",
                        steps: e.steps.map((st) =>
                          st.status === "waiting-approval" ? { ...st, status: "failed" as const, error: `Rejected: ${reason || "no reason given"}` } : st
                        ),
                      }
                    : e
                ),
              }));
              persistExecution(ap.executionId);
              useHive.getState().pushToast({ title: "Rejected", body: `${ap.action} · ${reason || "no reason"}`, tone: "warning" });
            })
            .catch(() => syncError("Rejection wasn't recorded — retry"));
        },

        escalate: (approvalId) => {
          const ap = get().approvals.find((a) => a.id === approvalId);
          set({ approvalModalId: null });
          useHive.getState().pushToast({ title: "Escalated", body: ap ? `${ap.action} → admin on-call` : "Escalated", tone: "info" });
        },

        // Drives ONLY client-initiated sandbox simulations. Real executions are
        // advanced by the engine server-side; their state is whatever the API says.
        tick: () =>
          set((s) => {
            let changed = false;
            const completed: string[] = [];
            const executions = s.executions.map((e) => {
              if (!e.simulated || e.status !== "running") return e;
              const idx = e.steps.findIndex((st) => st.status === "running");
              if (idx === -1) return e;
              const next = e.steps[idx + 1];
              if (e.progress < 0.92 && next && next.status === "pending") {
                changed = true;
                const steps = e.steps.map((st, i) => {
                  if (i === idx) return { ...st, status: "completed" as const, duration: st.duration === "running" ? "—" : st.duration };
                  if (i === idx + 1) return { ...st, status: "running" as const, at: "now", duration: "running" };
                  return st;
                });
                return { ...e, steps, currentStep: steps[idx + 1].label, progress: progressOf(steps) };
              }
              const stalled = e.steps[idx + 1]?.status === "waiting-approval" || e.steps[idx + 1]?.status === "pending";
              if (!next || (!stalled && idx === e.steps.length - 1)) {
                changed = true;
                completed.push(e.id);
                const steps = e.steps.map((st, i) => (i === idx ? { ...st, status: "completed" as const } : st));
                return { ...e, steps, status: "completed" as const, progress: 1, currentStep: "Done" };
              }
              return e;
            });
            // Persist finished simulations so the record survives reloads.
            completed.forEach((id) => {
              const exec = executions.find((e) => e.id === id);
              if (exec) void api.patch(`/api/v1/executions/${id}`, exec).catch(() => undefined);
            });
            return changed ? { executions } : {};
          }),
      };
    },
    {
      name: "hive-execution-ui",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        selectedExecutionId: s.selectedExecutionId,
        statusFilter: s.statusFilter,
      }),
    }
  )
);

function name(get: () => ExecutionState, id: string) {
  return get().executions.find((e) => e.id === id)?.name ?? "";
}
